import React, { useCallback } from 'react';

import { Button, HStack, Input as ChakraInput, useSlotRecipe } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons/linear';

import { ClearButtonProps, SearchLocationProps } from './search-location.props';

import { defaultSearchLocationTranslation } from '../../../models/search-location.translation.model';
import { Autocomplete } from '../autocomplete';
import { AutocompleteBaseItemModel } from '../autocomplete/autocomplete-base-item.model';

export const SearchLocation = <T extends AutocompleteBaseItemModel>({
    ref,
    autocompleteData,
    inputProps = {},
    translationData = defaultSearchLocationTranslation,
    setValue,
    wrapperProps = {},
    ...rest
}: SearchLocationProps<T> & {
    ref?: React.ForwardedRef<typeof ChakraInput>;
}): React.ReactNode => {
    const recipe = useSlotRecipe({
        key: 'searchLocation',
    });
    const styles = recipe();
    const { ref: inputRef, inputProps: innerInputProps, ...props } = inputProps;
    const { query, setQuery, onQueryChange } = autocompleteData;
    const handleClear = useCallback(() => {
        setQuery('');
        setValue('location', '');
        onQueryChange?.('');
    }, [setQuery, setValue, onQueryChange]);

    return (
        <HStack pos={'relative'} {...wrapperProps}>
            <Autocomplete
                {...autocompleteData}
                inputProps={{
                    leftIcon: <IconsLinear.Location size={'md'} {...styles.icon} />,
                    ref: inputRef,
                    ...props,
                    inputProps: {
                        placeholder: translationData.locationPlaceholder,
                        textOverflow: 'ellipsis',
                        pr: 6,
                        ...innerInputProps,
                    },
                }}
                {...rest}
            />
            {query ? (
                <ClearButton onClear={handleClear} label={translationData.clearLocation} />
            ) : null}
        </HStack>
    );
};

const ClearButton = ({ onClear, label }: ClearButtonProps) => (
    <Button
        variant={'link'}
        position={'absolute'}
        right={{ base: 2, md: 1.5 }}
        bottom={{ base: '12px', md: 'unset' }}
        onMouseDown={e => e.preventDefault()}
        onClick={onClear}
        aria-label={label}
    >
        <IconsLinear.Close size={'xs'} color={'gray.600'} />
    </Button>
);
